// src/components/StarRating.js
// Reusable star display + interactive input

const AMBER = '#F59E0B'

// ── Display only ──
export function StarDisplay({ rating = 0, size = 14, showEmpty = true }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24"
          fill={i <= Math.round(rating) ? AMBER : (showEmpty ? '#E5E7EB' : 'none')}
          stroke={i <= Math.round(rating) ? AMBER : '#D1D5DB'}
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ))}
    </div>
  )
}

// ── Interactive input ──
export function StarInput({ value, onChange, size = 32 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button" onClick={() => onChange(i)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', lineHeight: 0 }}>
          <svg width={size} height={size} viewBox="0 0 24 24"
            fill={i <= value ? AMBER : 'none'}
            stroke={i <= value ? AMBER : '#D1D5DB'}
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
            style={{ transition: 'all 0.1s' }}>
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        </button>
      ))}
    </div>
  )
}

// ── Rating summary bar (Google Maps style) ──
export function RatingBreakdown({ reviews = [] }) {
  const total = reviews.length
  const avg = total > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / total : 0
  const counts = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.rating === star).length,
    pct: total > 0 ? (reviews.filter(r => r.rating === star).length / total) * 100 : 0,
  }))

  return (
    <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
      {/* Big number */}
      <div style={{ textAlign: 'center', flexShrink: 0 }}>
        <div style={{ fontSize: '48px', fontWeight: 800, color: '#111827', lineHeight: 1, letterSpacing: '-2px' }}>
          {avg > 0 ? avg.toFixed(1) : '—'}
        </div>
        <StarDisplay rating={avg} size={16} />
        <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>{total} review{total !== 1 ? 's' : ''}</div>
      </div>

      {/* Breakdown bars */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {counts.map(({ star, count, pct }) => (
          <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: '#6B7280', width: '10px', textAlign: 'right', flexShrink: 0 }}>{star}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill={AMBER} stroke={AMBER} strokeWidth="1" style={{ flexShrink: 0 }}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            <div style={{ flex: 1, height: '8px', borderRadius: '4px', background: '#F3F4F6', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: '4px', background: AMBER, width: pct + '%', transition: 'width 0.4s ease' }} />
            </div>
            <span style={{ fontSize: '11.5px', color: '#9CA3AF', width: '20px', flexShrink: 0 }}>{count}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default StarDisplay